import * as React2 from 'react';
import { Badge, Textarea, cn, EmptyState, ErrorBoundary, GraphFrame, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuItem, Button, Input, Skeleton } from '@authe/ui-core';
import { CommentAnchorKind, WORKSPACE_ANCHOR, WorkspaceKind, CollaborationProvider, CommentThread, useSharedState, useCollabStatus, useOptionalSharedState, useCollaboration, useWorkspaceMeta, usePresence, PresenceAvatars, userColor } from '@authe/collaboration';
export { CommentAnchorKind, WORKSPACE_ANCHOR, WorkspaceKind, anchorFor } from '@authe/collaboration';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createLogger } from '@authe/fe-commons/logging';
import { customerTextEdit, customerText } from '@authe/fe-commons/evidence';
import { overlayEdit, hiddenRefs, hiddenCount, isAuthoredId, draftEdge, draftNode, applySceneOverlay, rebindReconstructions, reconnectReconstruction, draftReconstruction } from '@authe/fe-commons/graph-overlay';
import { emptySceneOverlay, layerForSubject, SUBJECT_ANY, parseSceneOverlay } from '@authe/fe-commons/models';
import { focusFromTransactionRow, focusNodeId, parseFocusNodeId } from '@authe/fe-commons/focus';
export { addressNodeId, entityNodeId, focusAddress, focusEntity, focusFromTransactionRow, focusNodeId, parseFocusNodeId, sameFocusTarget, txNodeId } from '@authe/fe-commons/focus';

// src/InvestigationWorkspace.tsx

// src/viewPlacement.ts
function placeViews(views, activeId) {
  const primary = views.filter((view) => view.placement !== "more");
  const more = views.filter((view) => view.placement === "more");
  return { primary, more, activeMore: more.find((view) => view.id === activeId) };
}
function sectionViews(views) {
  const sections = [];
  for (const view of views) {
    const current = sections.find((section) => section.name === view.section);
    if (current) current.views.push(view);
    else sections.push({ name: view.section, views: [view] });
  }
  return sections;
}
function viewFlagged(view) {
  return view.badge != null && view.badge !== false && view.badge !== 0 && view.badge !== "";
}

// src/viewRequest.ts
function viewRequestAction(request, honouredNonce, views) {
  if (!request || request.nonce === honouredNonce) return "none";
  return views.some((v) => v.id === request.id) ? "apply" : "pending";
}
var WorkspaceViewId = {
  TABLE: "table",
  GRAPH: "graph",
  SEMANTIC: "semantic",
  PIPELINE: "pipeline",
  NOTES: "notes"
};
function viewAnchor(viewId, label) {
  return { kind: CommentAnchorKind.CUSTOM, id: `view:${viewId}`, label: label ?? viewId };
}
function panelCommentAnchor(panel) {
  return panel.commentAnchor ?? WORKSPACE_ANCHOR;
}
var DEFAULT_LABELS = {
  table: "Table",
  graph: "Graph",
  semantic: "Semantic",
  pipeline: "Pipeline",
  notes: "Notes"
};
function standardInvestigationPanels(init) {
  const labels = { ...DEFAULT_LABELS, ...init.labels };
  const panels = [];
  if (init.table !== void 0) {
    panels.push({ id: WorkspaceViewId.TABLE, label: labels.table, content: init.table });
  }
  if (init.graph !== void 0) {
    panels.push({ id: WorkspaceViewId.GRAPH, label: labels.graph, content: init.graph });
  }
  if (init.semantic !== void 0) {
    panels.push({ id: WorkspaceViewId.SEMANTIC, label: labels.semantic, content: init.semantic });
  }
  if (init.pipeline !== void 0) {
    panels.push({ id: WorkspaceViewId.PIPELINE, label: labels.pipeline, content: init.pipeline });
  }
  return panels;
}
function WorkspaceProvider({
  scope,
  user,
  title,
  chainId,
  persist,
  readOnly,
  transport,
  fallback,
  errorFallback,
  children
}) {
  const meta = React2.useMemo(
    () => ({
      title: title ?? "Investigation",
      kind: WorkspaceKind.INVESTIGATION,
      ...chainId !== void 0 ? { chainId } : {}
    }),
    [title, chainId]
  );
  return /* @__PURE__ */ jsx(
    CollaborationProvider,
    {
      scope,
      user,
      transport,
      meta,
      persist,
      readOnly,
      errorFallback,
      fallback,
      children
    }
  );
}
function WorkspaceComments({
  anchor = WORKSPACE_ANCHOR,
  users,
  title = "Discussion",
  hideResolved,
  className
}) {
  return /* @__PURE__ */ jsx(
    CommentThread,
    {
      anchor,
      users,
      title,
      hideResolved,
      className
    }
  );
}
var WORKSPACE_PACKAGE = "workspace";
createLogger(`fe:${WORKSPACE_PACKAGE}`);
var DEFAULT_NOTES_KEY = "investigation:notes";
var GRAPH_ANNOTATIONS_KEY = "investigation:graph-annotations";
function NotesPanel({
  stateKey = DEFAULT_NOTES_KEY,
  title = "Notes",
  placeholder = "Capture findings, hypotheses, and next steps for everyone with access to this case.",
  readOnly,
  className
}) {
  const [notes, setNotes] = useSharedState(stateKey, "");
  const delivery = useCollabStatus();
  const documentStatus = delivery.documentStatus ?? delivery.status;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex h-full min-h-0 flex-col gap-2 p-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold", children: title }),
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "font-normal", children: documentStatus === "live" ? "Document synced" : documentStatus === "connecting" ? "Document connecting" : documentStatus === "failed" ? "Document sync interrupted" : "This browser only" })
    ] }),
    /* @__PURE__ */ jsx(
      Textarea,
      {
        "aria-label": title,
        value: customerText(notes),
        onChange: (event) => setNotes(customerTextEdit(notes, event.target.value)),
        placeholder,
        readOnly,
        spellCheck: true,
        className: "min-h-0 flex-1 resize-none font-mono text-sm leading-relaxed"
      }
    ),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Markdown-friendly. Shared with people who can access this case when document sync is connected (last edit wins)." })
  ] });
}
function WorkspaceLoading() {
  return /* @__PURE__ */ jsxs("div", { className: "flex h-full min-h-0 flex-col gap-3 p-4", "aria-busy": true, children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-56" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-72" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "flex-1" }),
    /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Connecting to the workspace\u2026" })
  ] });
}
function TabPresenceDots({ peers }) {
  return /* @__PURE__ */ jsx("span", { className: "ml-0.5 flex -space-x-1", "aria-hidden": true, children: peers.slice(0, 3).map((peer) => /* @__PURE__ */ jsx(
    "span",
    {
      title: peer.user.name,
      className: "size-2 rounded-full ring-1 ring-background",
      style: { background: userColor(peer.user) }
    },
    peer.clientId
  )) });
}
function WorkspaceTab({
  view,
  active,
  peers,
  onSelect
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      role: "tab",
      "aria-selected": active,
      onClick: onSelect,
      className: cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors",
        active ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
      ),
      children: [
        view.icon ? /* @__PURE__ */ jsx("span", { className: "shrink-0", children: view.icon }) : null,
        /* @__PURE__ */ jsx("span", { children: view.label }),
        view.badge != null && view.badge !== false ? /* @__PURE__ */ jsx(ViewBadge, { children: view.badge }) : null,
        peers.length > 0 ? /* @__PURE__ */ jsx(TabPresenceDots, { peers }) : null
      ]
    }
  );
}
function ViewBadge({ children }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { className: "sr-only", children: ", " }),
    /* @__PURE__ */ jsx("span", { className: "ml-0.5 rounded-full bg-muted-foreground/15 px-1.5 text-3xs font-semibold leading-4 tabular-nums text-muted-foreground", children })
  ] });
}
function MoreViewsMenu({
  views,
  activeId,
  onSelect
}) {
  const sections = sectionViews(views);
  const flagged = views.some(viewFlagged);
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        "aria-label": "More views",
        className: "inline-flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
        children: [
          /* @__PURE__ */ jsx("span", { children: "More" }),
          flagged ? /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "size-1.5 rounded-full bg-info" }) : null,
          /* @__PURE__ */ jsx("svg", { "aria-hidden": true, viewBox: "0 0 20 20", className: "size-3.5 fill-current", children: /* @__PURE__ */ jsx("path", { d: "M5.3 7.3a1 1 0 0 1 1.4 0L10 10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4Z" }) })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(DropdownMenuContent, { align: "start", className: "min-w-60", children: sections.map((section, index) => /* @__PURE__ */ jsxs(React2.Fragment, { children: [
      index > 0 ? /* @__PURE__ */ jsx(DropdownMenuSeparator, {}) : null,
      section.name ? /* @__PURE__ */ jsx(DropdownMenuLabel, { children: section.name }) : null,
      section.views.map((view) => /* @__PURE__ */ jsxs(
        DropdownMenuItem,
        {
          onSelect: () => onSelect(view.id),
          "aria-current": view.id === activeId ? "page" : void 0,
          className: cn("flex items-start gap-2", view.id === activeId && "bg-muted"),
          children: [
            view.icon ? /* @__PURE__ */ jsx("span", { className: "mt-0.5 shrink-0 text-muted-foreground", children: view.icon }) : null,
            /* @__PURE__ */ jsxs("span", { className: "flex min-w-0 flex-1 flex-col", children: [
              /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx("span", { children: view.label }),
                view.badge != null && view.badge !== false ? /* @__PURE__ */ jsx(ViewBadge, { children: view.badge }) : null
              ] }),
              view.hint ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: view.hint }) : null
            ] })
          ]
        },
        view.id
      ))
    ] }, section.name ?? `section-${index}`)) })
  ] });
}
function InvestigationWorkspaceFrame({
  panels: views,
  title,
  headerExtra,
  sidebar,
  subheader,
  defaultView,
  viewRequest,
  onViewChange,
  peersForView,
  className
}) {
  const firstId = views[0]?.id ?? "";
  const initialId = defaultView && views.some((v) => v.id === defaultView) ? defaultView : firstId;
  const [activeView, setActiveView] = React2.useState(initialId);
  const [mounted, setMounted] = React2.useState(() => new Set(initialId ? [initialId] : []));
  React2.useEffect(() => {
    if (views.length === 0) return;
    if (!views.some((v) => v.id === activeView)) {
      setActiveView(views[0].id);
      setMounted((prev) => new Set(prev).add(views[0].id));
    }
  }, [views, activeView]);
  const activePanel = views.find((v) => v.id === activeView);
  const { primary: primaryViews, more: moreViews, activeMore: activeMoreView } = placeViews(views, activeView);
  const [honouredNonce, setHonouredNonce] = React2.useState(null);
  const pendingRequest = React2.useRef(null);
  const requestAction = viewRequestAction(viewRequest, honouredNonce, views);
  pendingRequest.current = requestAction === "pending" ? viewRequest ?? null : null;
  const select = React2.useCallback((id) => {
    if (pendingRequest.current) setHonouredNonce(pendingRequest.current.nonce);
    setActiveView(id);
    setMounted((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  if (viewRequest && requestAction === "apply") {
    setHonouredNonce(viewRequest.nonce);
    setActiveView(viewRequest.id);
    setMounted((prev) => prev.has(viewRequest.id) ? prev : new Set(prev).add(viewRequest.id));
  }
  const reportedView = React2.useRef(activeView);
  React2.useEffect(() => {
    if (reportedView.current === activeView) return;
    reportedView.current = activeView;
    onViewChange?.(activeView);
  }, [activeView, onViewChange]);
  const contentCache = React2.useRef(/* @__PURE__ */ new Map());
  const renderContent = React2.useCallback((view) => {
    if (typeof view.content !== "function") return view.content;
    const cached = contentCache.current.get(view.id);
    if (cached !== void 0) return cached;
    const node = view.content();
    contentCache.current.set(view.id, node);
    return node;
  }, []);
  if (views.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex h-full min-h-0 items-center justify-center", className), children: /* @__PURE__ */ jsx(
      EmptyState,
      {
        size: "lg",
        title: "Nothing to investigate yet",
        description: "Add table, graph, pipeline, or custom panels to this workspace."
      }
    ) });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex h-full min-h-0 flex-col bg-background", className), children: [
    /* @__PURE__ */ jsxs("header", { className: "flex flex-wrap items-center gap-3 border-b px-3 py-2", children: [
      title,
      /* @__PURE__ */ jsx("div", { className: "ml-auto flex min-w-0 flex-wrap items-center gap-2", children: typeof headerExtra === "function" ? headerExtra(activePanel) : headerExtra })
    ] }),
    subheader ? /* @__PURE__ */ jsx("div", { className: "border-b px-3 py-1.5", children: subheader }) : null,
    /* @__PURE__ */ jsxs(
      "nav",
      {
        role: "tablist",
        "aria-label": "Workspace views",
        className: "flex items-center gap-1 overflow-x-auto border-b px-2 py-1.5",
        children: [
          primaryViews.map((view) => /* @__PURE__ */ jsx(
            WorkspaceTab,
            {
              view,
              active: view.id === activeView,
              peers: peersForView?.(view.id) ?? [],
              onSelect: () => select(view.id)
            },
            view.id
          )),
          activeMoreView ? /* @__PURE__ */ jsx(
            WorkspaceTab,
            {
              view: activeMoreView,
              active: true,
              peers: peersForView?.(activeMoreView.id) ?? [],
              onSelect: () => select(activeMoreView.id)
            },
            activeMoreView.id
          ) : null,
          moreViews.length > 0 ? /* @__PURE__ */ jsx(MoreViewsMenu, { views: moreViews, activeId: activeView, onSelect: select }) : null
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex min-h-0 flex-1", children: [
      /* @__PURE__ */ jsx("div", { className: "relative min-w-0 flex-1", children: views.map((view) => {
        const isActive = view.id === activeView;
        const keepMounted = view.keepMounted !== false;
        const shouldRender = isActive || mounted.has(view.id) && keepMounted;
        if (!shouldRender) return null;
        return /* @__PURE__ */ jsx(
          "div",
          {
            role: "tabpanel",
            "aria-label": view.label,
            hidden: !isActive,
            className: "absolute inset-0 overflow-hidden",
            children: /* @__PURE__ */ jsx(ErrorBoundary, { boundary: `workspace:view:${view.id}`, children: renderContent(view) })
          },
          view.id
        );
      }) }),
      typeof sidebar === "function" ? sidebar(activePanel) : sidebar
    ] })
  ] });
}
function WorkspaceFrame(props) {
  const { readOnly } = useCollaboration();
  const { panels, notes, notesKey, comments, showPresence, users, editableTitle, fallbackTitle } = props;
  const views = React2.useMemo(() => notes ? [...panels, {
    id: WorkspaceViewId.NOTES,
    label: "Notes",
    content: /* @__PURE__ */ jsx(NotesPanel, { stateKey: notesKey, readOnly }),
    commentAnchor: viewAnchor(WorkspaceViewId.NOTES, "Notes")
  }] : panels, [panels, notes, notesKey, readOnly]);
  const [commentsOpen, setCommentsOpen] = React2.useState(props.commentsOpenByDefault);
  const { meta, setTitle } = useWorkspaceMeta();
  const canonicalTitle = props.onTitleChange ? fallbackTitle : meta.title ?? fallbackTitle;
  const [titleDraft, setTitleDraft] = React2.useState(canonicalTitle);
  const [editingTitle, setEditingTitle] = React2.useState(false);
  React2.useEffect(() => {
    if (!editingTitle) setTitleDraft(canonicalTitle);
    if (props.onTitleChange && !readOnly && meta.title !== fallbackTitle) setTitle(fallbackTitle);
  }, [canonicalTitle, editingTitle, props.onTitleChange, fallbackTitle, readOnly, meta.title, setTitle]);
  const presence = usePresence({ includeSelf: false });
  const [activeView, setActiveView] = React2.useState(props.defaultView ?? views[0]?.id ?? "");
  const { setFocus } = presence;
  const activeLabel = views.find((v) => v.id === activeView)?.label;
  React2.useEffect(() => {
    if (!activeView || !showPresence) return;
    setFocus(viewAnchor(activeView, activeLabel));
    return () => setFocus(null);
  }, [activeView, activeLabel, setFocus, showPresence]);
  return /* @__PURE__ */ jsx(
    InvestigationWorkspaceFrame,
    {
      panels: views,
      defaultView: props.defaultView,
      viewRequest: props.viewRequest,
      onViewChange: (id) => {
        setActiveView(id);
        props.onViewChange?.(id);
      },
      className: props.className,
      subheader: props.subheader,
      peersForView: showPresence ? (id) => presence.focusedOn(viewAnchor(id)) : void 0,
      title: editableTitle && !readOnly ? /* @__PURE__ */ jsx(
        Input,
        {
          value: titleDraft,
          onFocus: () => setEditingTitle(true),
          onChange: (event) => setTitleDraft(event.target.value),
          onBlur: () => {
            setEditingTitle(false);
            const next = titleDraft.trim();
            if (next && next !== canonicalTitle) (props.onTitleChange ?? setTitle)(next);
            else setTitleDraft(canonicalTitle);
          },
          onKeyDown: (event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          },
          placeholder: fallbackTitle,
          "aria-label": "Investigation title",
          className: "h-8 min-w-48 flex-1 basis-72 border-transparent px-1 text-sm font-semibold shadow-none hover:border-input focus-ring"
        }
      ) : /* @__PURE__ */ jsx("h2", { className: "min-w-0 flex-1 basis-72 break-words text-sm font-semibold", children: canonicalTitle }),
      headerExtra: (active) => /* @__PURE__ */ jsxs(Fragment, { children: [
        props.headerExtra,
        showPresence && /* @__PURE__ */ jsx(PresenceAvatars, { max: 6 }),
        comments && !active?.hideComments && /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            size: "sm",
            variant: commentsOpen ? "secondary" : "ghost",
            onClick: () => setCommentsOpen((open) => !open),
            children: "Comments"
          }
        )
      ] }),
      sidebar: comments && commentsOpen ? (active) => /* @__PURE__ */ jsx("aside", { className: "w-80 shrink-0 overflow-auto border-l p-3", children: /* @__PURE__ */ jsx(WorkspaceComments, { anchor: active ? panelCommentAnchor(active) : void 0, users }) }) : void 0
    }
  );
}
function InvestigationWorkspace(props) {
  const {
    scope,
    user,
    users,
    persist,
    transport,
    fallback,
    errorFallback,
    title,
    chainId,
    editableTitle = true,
    readOnly = false,
    onTitleChange,
    panels,
    notes = true,
    notesKey,
    comments = true,
    showPresence = true,
    defaultView,
    viewRequest,
    commentsOpenByDefault = false,
    onViewChange,
    headerExtra,
    subheader,
    children,
    className
  } = props;
  const resolvedUsers = users ?? [user];
  return /* @__PURE__ */ jsxs(
    WorkspaceProvider,
    {
      scope,
      user,
      title,
      chainId,
      persist,
      readOnly,
      transport,
      errorFallback,
      fallback: fallback ?? /* @__PURE__ */ jsx(WorkspaceLoading, {}),
      children: [
        /* @__PURE__ */ jsx(
          WorkspaceFrame,
          {
            panels,
            notes,
            notesKey,
            comments,
            showPresence,
            commentsOpenByDefault,
            users: resolvedUsers,
            editableTitle,
            fallbackTitle: title ?? "Investigation",
            onTitleChange,
            defaultView,
            viewRequest,
            onViewChange,
            headerExtra,
            subheader,
            className
          }
        ),
        children
      ]
    }
  );
}
var PERSIST_DEBOUNCE_MS = 1500;
function useGraphAnnotations(options = {}) {
  const { subjectKey, readOnly = false, persisted, onPersist } = options;
  const [stored, setStored] = useOptionalSharedState(
    annotationRoomKey(subjectKey, options.stateKey),
    EMPTY
  );
  const durable = React2.useMemo(
    () => layerForSubject(persisted, subjectKey ?? SUBJECT_ANY),
    [persisted, subjectKey]
  );
  const overlay = React2.useMemo(() => normalize(stored), [stored]);
  useDurableMirror({
    overlay,
    persisted: durable,
    onPersist,
    subjectKey: subjectKey ?? SUBJECT_ANY,
    readOnly,
    setStored
  });
  const edit = React2.useCallback(
    (fn) => {
      if (readOnly) return;
      setStored((current) => fn(normalize(current)));
    },
    [readOnly, setStored]
  );
  const actions = React2.useMemo(
    () => ({
      // Minted here rather than in the caller for the same reason the edge is:
      // the id has to be the one that lands in the overlay, and nothing else
      // identifies the node until the shared doc round-trips.
      onAddNode: (draft) => {
        if (readOnly) return null;
        const node = draftNode(draft);
        edit((o) => overlayEdit.addNode(o, node));
        return node;
      },
      // Returns the edge it drew so the caller can put the author in front of it
      // and ask what it means. Minted here rather than in the caller because the
      // id has to be the one that lands in the overlay; nothing else identifies
      // the edge until the shared doc round-trips.
      onConnect: (source, target) => {
        if (readOnly) return null;
        const edge = draftEdge({ source, target });
        edit((o) => overlayEdit.addEdge(o, edge));
        return edge;
      },
      onRelabel: (target, label) => edit((o) => overlayEdit.relabel(o, target, label)),
      onExplain: (target, reason) => edit((o) => overlayEdit.explain(o, target, reason)),
      onMove: (nodeId, position) => edit((o) => overlayEdit.moveNode(o, nodeId, position)),
      onHide: (target) => edit((o) => overlayEdit.hide(o, target)),
      // Removing is only meaningful for the layer's own elements; a derived
      // element is hidden instead, so the decode stays intact.
      onRemove: (target) => edit((o) => isAuthoredId(target.id) ? overlayEdit.remove(o, target) : o),
      onUnhide: (target) => edit((o) => overlayEdit.unhide(o, target)),
      // Defaults to the label: "restore the decoded name" is the undo an analyst
      // reaches for, and dropping a typed reason along with it would take back
      // more than they asked to.
      onRevert: (target, fields = ["label"]) => edit((o) => overlayEdit.revert(o, target, fields)),
      onRestoreHidden: () => edit(overlayEdit.restoreHidden),
      hiddenCount: hiddenCount(overlay),
      hidden: hiddenRefs(overlay),
      isPatched: (target, field) => overlayEdit.isPatched(overlay, target, field),
      reasonFor: (target) => overlayEdit.reasonOf(overlay, target)
    }),
    [edit, overlay, readOnly]
  );
  const apply = React2.useCallback(
    (scene) => applySceneOverlay(scene, [rebindReconstructions(overlay, scene.nodes)]),
    [overlay]
  );
  const clear = React2.useCallback(() => edit(() => emptySceneOverlay()), [edit]);
  return {
    ...actions,
    onReconstruct: (draft) => {
      if (readOnly) return null;
      const { node, edge } = draftReconstruction(draft);
      edit((current) => overlayEdit.addEdge(overlayEdit.addNode(current, node), edge));
      return node;
    },
    onReconnectReconstruction: (nodeId, anchor) => edit((current) => reconnectReconstruction(current, nodeId, anchor)),
    overlay,
    apply,
    isEmpty: countElements(overlay) === 0,
    clear
  };
}
function annotationRoomKey(subjectKey, override) {
  if (override) return override;
  return subjectKey ? `${GRAPH_ANNOTATIONS_KEY}:${subjectKey}` : GRAPH_ANNOTATIONS_KEY;
}
function restoreStep(overlay, persisted) {
  if (countElements(overlay) > 0) return { action: "settle" };
  const durable = normalize(persisted ?? void 0);
  return countElements(durable) > 0 ? { action: "seed", overlay: durable } : { action: "wait" };
}
function needsMirror(overlay, persisted) {
  return overlayFingerprint(overlay) !== overlayFingerprint(normalize(persisted ?? void 0));
}
function overlayFingerprint(overlay) {
  let normalized;
  try {
    normalized = parseSceneOverlay(overlay);
  } catch {
    normalized = normalize(overlay);
  }
  return JSON.stringify(normalized, (_key, value) => value && typeof value === "object" && !Array.isArray(value) ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b))) : value);
}
function useDurableMirror(input) {
  const { overlay, persisted, onPersist, subjectKey, readOnly, setStored } = input;
  const settledFor = React2.useRef(null);
  React2.useEffect(() => {
    if (readOnly || settledFor.current === subjectKey) return;
    const step = restoreStep(overlay, persisted);
    if (step.action === "wait") return;
    settledFor.current = subjectKey;
    if (step.action === "seed") setStored(() => step.overlay);
  }, [overlay, persisted, readOnly, setStored, subjectKey]);
  const persist = React2.useRef(onPersist);
  React2.useEffect(() => {
    persist.current = onPersist;
  });
  React2.useEffect(() => {
    if (readOnly || !onPersist) return void 0;
    if (!needsMirror(overlay, persisted)) return void 0;
    const handle = window.setTimeout(
      () => persist.current?.(overlay, subjectKey),
      PERSIST_DEBOUNCE_MS
    );
    return () => window.clearTimeout(handle);
  }, [overlay, persisted, subjectKey, readOnly, Boolean(onPersist)]);
}
var EMPTY = Object.freeze(emptySceneOverlay());
function normalize(body) {
  if (!body) return EMPTY;
  return {
    nodes: body.nodes ?? [],
    edges: body.edges ?? [],
    groups: body.groups ?? [],
    hidden_node_ids: body.hidden_node_ids ?? [],
    hidden_edge_ids: body.hidden_edge_ids ?? [],
    node_patches: body.node_patches ?? {},
    edge_patches: body.edge_patches ?? {},
    positions: body.positions ?? {},
    // Authorship is stamped by the API from the caller's identity when the layer
    // is published, never asserted from the client — so the live layer carries
    // the keys and leaves them empty rather than guessing at them.
    authored_by: body.authored_by ?? null,
    authored_at: body.authored_at ?? null,
    authored_reason: body.authored_reason ?? null,
    authored_scope: body.authored_scope ?? null
  };
}
function countElements(overlay) {
  return overlay.nodes.length + overlay.edges.length + overlay.groups.length + overlay.hidden_node_ids.length + overlay.hidden_edge_ids.length + Object.keys(overlay.node_patches).length + Object.keys(overlay.edge_patches).length + Object.keys(overlay.positions).length;
}

// src/graph/graphAuthoring.ts
function graphAuthoringFrom(annotations) {
  const {
    onAddNode,
    onConnect,
    onRelabel,
    onExplain,
    onMove,
    onHide,
    onRemove,
    onUnhide,
    onRevert,
    onRestoreHidden,
    hiddenCount,
    hidden,
    isPatched,
    reasonFor
  } = annotations;
  return {
    onAddNode,
    onConnect,
    onRelabel,
    onExplain,
    onMove,
    onHide,
    onRemove,
    onUnhide,
    onRevert,
    onRestoreHidden,
    hiddenCount,
    hidden,
    isPatched,
    reasonFor
  };
}
var FocusContext = React2.createContext(null);
function InvestigationFocusProvider({
  focus,
  onFocusChange,
  defaultFocus = null,
  children
}) {
  const [internal, setInternal] = React2.useState(defaultFocus);
  const isControlled = focus !== void 0;
  const current = isControlled ? focus : internal;
  const setFocus = React2.useCallback(
    (target) => {
      if (!isControlled) setInternal(target);
      onFocusChange?.(target);
    },
    [isControlled, onFocusChange]
  );
  const value = React2.useMemo(
    () => ({ focus: current, setFocus, clearFocus: () => setFocus(null) }),
    [current, setFocus]
  );
  return /* @__PURE__ */ jsx(FocusContext.Provider, { value, children });
}
function useInvestigationFocus() {
  const ctx = React2.useContext(FocusContext);
  if (!ctx) throw new Error("useInvestigationFocus must be used within an <InvestigationFocusProvider>");
  return ctx;
}
function useOptionalInvestigationFocus() {
  return React2.useContext(FocusContext);
}
function useTableFocusProps(options = {}) {
  const { focus, setFocus } = useInvestigationFocus();
  const userClick = options.onRowClick;
  const activeRowId = focus?.kind === "transaction" ? focus.id : void 0;
  const onRowClick = React2.useCallback(
    (row) => {
      const target = focusFromTransactionRow(row);
      if (target) setFocus(target);
      userClick?.(row);
    },
    [setFocus, userClick]
  );
  return React2.useMemo(() => ({ activeRowId, onRowClick }), [activeRowId, onRowClick]);
}
function focusTargetForNodeClick(nodeId, parseNodeId) {
  if (parseNodeId) return parseNodeId(nodeId);
  return parseFocusNodeId(nodeId) ?? { kind: "transaction", id: nodeId };
}
function useGraphFocusProps(options = {}) {
  const { focus, setFocus } = useInvestigationFocus();
  const { onNodeClick: userClick, toNodeId, parseNodeId } = options;
  const nodeId = focus ? toNodeId ? toNodeId(focus) : focusNodeId(focus) : void 0;
  const onNodeClick = React2.useCallback(
    (node) => {
      const target = focusTargetForNodeClick(node.id, parseNodeId);
      if (target) setFocus(target);
      userClick?.(node);
    },
    [parseNodeId, setFocus, userClick]
  );
  return React2.useMemo(() => ({ focusNodeId: nodeId, onNodeClick }), [nodeId, onNodeClick]);
}
function PanelLoading({ label }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex h-full min-h-0 flex-col gap-3 p-4", "aria-busy": true, children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-40" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-full" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-5/6" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "flex-1" }),
    /* @__PURE__ */ jsxs("span", { className: "sr-only", children: [
      "Loading ",
      label,
      "\u2026"
    ] })
  ] });
}
function LazyPanel({ label, className, children }) {
  return /* @__PURE__ */ jsx("div", { className: cn("h-full min-h-0", className), children: /* @__PURE__ */ jsx(ErrorBoundary, { boundary: `workspace:${label}`, children: /* @__PURE__ */ jsx(React2.Suspense, { fallback: /* @__PURE__ */ jsx(PanelLoading, { label }), children }) }) });
}
var LazyTransactionTable = React2.lazy(
  () => import('@authe/tx-table').then((m) => ({ default: m.TransactionTable }))
);
function TablePanel(props) {
  return /* @__PURE__ */ jsx(LazyPanel, { label: "Table", children: /* @__PURE__ */ jsx("div", { className: "h-full overflow-auto p-3", children: /* @__PURE__ */ jsx(LazyTransactionTable, { ...props }) }) });
}
var LazyGraphCanvas = React2.lazy(
  () => import('@authe/tx-graph').then((m) => ({ default: m.GraphCanvas }))
);
function GraphPanel(props) {
  return (
    // A frame, like `ScenePanel`: `GraphCanvas` is `h-full w-full`, which needs a
    // box whose height a percentage can resolve against — the frame guarantees
    // one however the host sized the panel.
    /* @__PURE__ */ jsx(GraphFrame, { className: "size-full", children: /* @__PURE__ */ jsx(LazyPanel, { label: "Graph", children: /* @__PURE__ */ jsx(LazyGraphCanvas, { ...props }) }) })
  );
}
var LazySceneGraphView = React2.lazy(
  () => import('@authe/tx-graph').then((m) => ({ default: m.SceneGraphView }))
);
function ScenePanel(props) {
  return (
    // The panel's box IS the graph's box: a frame, so the scene renderer fills
    // the tab it was mounted in instead of drawing its own fixed height inside
    // it — and so no host has to pass `height="100%"`. Outside `LazyPanel`, so
    // the box exists while the (heavy) graph chunk is still loading and the
    // skeleton fills it too.
    /* @__PURE__ */ jsx(GraphFrame, { className: "size-full", children: /* @__PURE__ */ jsx(LazyPanel, { label: "Graph", children: /* @__PURE__ */ jsx(LazySceneGraphView, { ...props }) }) })
  );
}
var LazySemanticView = React2.lazy(
  () => import('@authe/tx-semantic').then((m) => ({ default: m.SemanticView }))
);
function SemanticPanel(props) {
  return /* @__PURE__ */ jsx(LazyPanel, { label: "Semantic", children: /* @__PURE__ */ jsx("div", { className: "size-full overflow-auto p-4", children: /* @__PURE__ */ jsx(LazySemanticView, { ...props }) }) });
}
var LazyPipelineBuilderStudio = React2.lazy(
  () => import('@authe/pipeline-builder').then((m) => ({ default: m.PipelineBuilderStudio }))
);
function PipelinePanel(props) {
  return /* @__PURE__ */ jsx(LazyPanel, { label: "Pipeline", children: /* @__PURE__ */ jsx("div", { className: "size-full", children: /* @__PURE__ */ jsx(LazyPipelineBuilderStudio, { ...props }) }) });
}

export { DEFAULT_NOTES_KEY, GRAPH_ANNOTATIONS_KEY, GraphPanel, InvestigationFocusProvider, InvestigationWorkspace, InvestigationWorkspaceFrame, LazyPanel, NotesPanel, PipelinePanel, ScenePanel, SemanticPanel, TablePanel, WORKSPACE_PACKAGE, WorkspaceComments, WorkspaceProvider, WorkspaceViewId, graphAuthoringFrom, panelCommentAnchor, placeViews, sectionViews, standardInvestigationPanels, useGraphAnnotations, useGraphFocusProps, useInvestigationFocus, useOptionalInvestigationFocus, useTableFocusProps, viewAnchor, viewFlagged };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map